/* eslint-disable max-lines */
import { ContextStore, ExperimentStore } from 'bernie-plugin-mobx';
import { inject } from 'mobx-react';
import * as React from 'react';

import {
  DateField,
  DateFieldData,
  FormInputs,
  FormInputsProvider,
  FormQueryVariables,
  LodgingFormEvent,
  SubmitButton,
  SubmitButtonData,
  TravelersData,
  TravelersField,
  TravelersFieldTriggerType,
  useFormAction,
} from '@shared-ui/lodging-form';
import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { PropertyAvailability } from '@shared-ui/retail-lodging-property-availability';
import { useBexApi } from '@shared-ui/bex-api-context';
import { useLocalization } from '@shared-ui/localization-context';
import { useRefContext } from '@shared-ui/ref-context';
import { ViewLarge, Viewport, ViewSmall } from '@shared-ui/viewport-context';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { getDateRangeInput, getRooms } from 'stores/adapters';
import { SearchCriteriaModel } from 'stores/models';

import { isPetsAmenityFilterPresent } from 'src/common/utils/pets-utils';

import { ActionProviderDatedMobile } from './action-provider';
import { SharedUiTravelersSelectorWrapper } from '../offers/shared-ui-traveler-selector-wrapper';

export const START_DATE_REF_NAME = 'startDate';
export const END_DATE_REF_NAME = 'endDate';

/**
 * Translate a Date object into a DateInput
 * @protected
 */
export const parseDate = (date: Date) => ({
  day: date.getDate(),
  month: date.getMonth() + 1,
  year: date.getFullYear(),
});

export const SharedUiDateFieldWrapper: React.FC<{
  buttonText: string;
  closeOnSubmit?: boolean;
  onDismiss?: () => void;
  invalidDatesText?: string;
  context?: ContextStore;
  experiment?: ExperimentStore;
  currentHotel?: CurrentHotelStore;
}> = inject(
  'context',
  'experiment',
  'currentHotel'
)(({ experiment, context, currentHotel, buttonText, onDismiss, closeOnSubmit = true, invalidDatesText }) => {
  const { registerTarget } = useRefContext();
  const hotelId = currentHotel?.hotelId;
  const searchCriteria = currentHotel?.searchCriteria;
  const dateRangeInput = searchCriteria && getDateRangeInput(searchCriteria);
  // Register targets so that datepicker can be opened from different parts of the page
  const startDateRef = registerTarget<HTMLElement>(START_DATE_REF_NAME);
  const endDateRef = registerTarget<HTMLElement>(END_DATE_REF_NAME);

  const dispatch = useFormAction();
  const [placeholder, setPlaceholder] = React.useState(context?.localDateFormat);
  const { formatText } = useLocalization();

  React.useEffect(() => {
    setPlaceholder(formatText('hotels.search.form.toDate.placeholder'));
  }, []);

  const dateFieldData: DateFieldData = React.useMemo(() => {
    const isVrbex = experiment?.exposures?.Vrbex_generic?.bucket === 1;
    const isVrbex28PlusDayStay = experiment?.exposures?.vrbex_28_plus_day_stays?.bucket === 1;

    const output: DateFieldData = {
      startId: 'hotels-check-in',
      startLabel: formatText('hotels.search.form.fromDateHyphen.label'),
      startName: 'startDate',
      startPlaceholder: placeholder || '',
      startInvalid: '',
      endId: 'hotels-check-out',
      endLabel: formatText('hotels.search.form.toDateHyphen.label'),
      endName: 'endDate',
      endPlaceholder: placeholder || '',
      endInvalid: '',
      buttonText,
      daysBookableInAdvance: SearchCriteriaModel.NUM_OF_DAYS_BOOKABLE_IN_ADVANCE,
      midnightBookingOffset: SearchCriteriaModel.MIDNIGHT_BOOKING_OFFSET_IN_HOURS,
      __typename: 'LodgingFormDateField',
      validations: [
        {
          __typename: 'StartDateRequiredValidation',
          error: invalidDatesText || formatText('hotels.search.validation.invalidFromDate.text'),
          events: [LodgingFormEvent.ON_CHANGE, LodgingFormEvent.ON_SUBMIT],
        },
        {
          __typename: 'EndDateRequiredValidation',
          error: invalidDatesText || formatText('hotels.search.validation.invalidToDate.text'),
          events: [LodgingFormEvent.ON_CHANGE, LodgingFormEvent.ON_SUBMIT],
        },
        {
          __typename: 'DateOrderValidation',
          error: formatText('hotels.search.validation.invalidDateRange.text'),
          events: [LodgingFormEvent.ON_LOAD, LodgingFormEvent.ON_CHANGE, LodgingFormEvent.ON_SUBMIT],
        },
      ],
    };
    if (!isVrbex || !isVrbex28PlusDayStay) {
      output.validations?.push({
        __typename: 'MaxDateRangeValidation',
        error: formatText('hotels.search.validation.invalidDateDateRangeLength.text'),
        maxDateRange: 28,
        events: [LodgingFormEvent.ON_LOAD, LodgingFormEvent.ON_CHANGE, LodgingFormEvent.ON_SUBMIT],
      });
    }

    return output;
  }, [
    buttonText,
    experiment?.exposures?.Vrbex_generic?.bucket,
    experiment?.exposures?.vrbex_28_plus_day_stays?.bucket,
    formatText,
    invalidDatesText,
    placeholder,
  ]);

  const onSubmitCB = (startDate: Date | null, endDate: Date | null): void => {
    dispatch({
      type: 'change',
      field: 'dates',
      value: {
        dates: {
          checkInDate: startDate ? (parseDate(startDate) as any) : null,
          checkOutDate: endDate ? (parseDate(endDate) as any) : null,
        },
      },
    });
  };

  const dateRange = dateRangeInput ? { end: dateRangeInput.checkOutDate, start: dateRangeInput.checkInDate } : null;

  return (
    <Experiment name="Vrbex_Availability_Date_Selector">
      <ExperimentControl>
        <Viewport>
          <ViewSmall>
            <DateField
              fieldName="dates"
              data={dateFieldData}
              closeOnSubmit={closeOnSubmit}
              onDismiss={onDismiss}
              isOpenOverride={true}
              startDateRef={startDateRef}
              endDateRef={endDateRef}
            />
          </ViewSmall>
          <ViewLarge>
            <DateField
              fieldName="dates"
              data={dateFieldData}
              closeOnSubmit={closeOnSubmit}
              startDateRef={startDateRef}
              endDateRef={endDateRef}
            />
          </ViewLarge>
        </Viewport>
      </ExperimentControl>
      <ExperimentVariant bucket={1}>
        <Viewport>
          <ViewSmall>
            <PropertyAvailability
              startDateRef={startDateRef}
              endDateRef={endDateRef}
              inputs={{ eid: String(hotelId), dateRange }}
              onSubmit={onSubmitCB}
              skipSsr={true}
            />
          </ViewSmall>
          <ViewLarge>
            <PropertyAvailability
              startDateRef={startDateRef}
              endDateRef={endDateRef}
              inputs={{ eid: String(hotelId), dateRange }}
              onSubmit={onSubmitCB}
              skipSsr={true}
            />
          </ViewLarge>
        </Viewport>
      </ExperimentVariant>
    </Experiment>
  );
});

export const SharedUiTravelersWrapper: React.FC<{
  context?: ContextStore;
  currentHotel?: CurrentHotelStore;
  doneLabel?: string;
  closeOnSubmit?: boolean;
  trigger?: TravelersFieldTriggerType;
  isSingleUnit?: boolean;
}> = inject(
  'context',
  'currentHotel'
)(({ context, currentHotel, doneLabel, closeOnSubmit = true, trigger = TravelersFieldTriggerType.INPUT, isSingleUnit = false }) => {
  const { formatText } = useLocalization();

  const travelersData: TravelersData = {
    __typename: 'LodgingFormTravelers',
    label: formatText('hotels.search.form.travelers.label'),
    travelersSummary: {
      __typename: 'TravelersSummary',
      roomsAndGuests: isSingleUnit ? 'hotels.roomPicker.roomSummary.singleUnit' : 'hotels.roomPicker.roomSummary',
      roomsAndGuestsWithPets: isSingleUnit ? 'hotels.roomPicker.roomSummary.singleUnit.withPets' : '',
    },
    roomPicker: {
      __typename: 'RoomPicker',
      toolBarIconLabel: formatText('hotels.roomPicker.cancelButton.a11yLabel'),
      toolBarTitle: formatText('hotels.roomPicker.title'),
      addRoomText: isSingleUnit ? '' : formatText('hotels.roomPicker.addRoomButton.label'),
      doneLabel: doneLabel || formatText('hotels.roomPicker.doneButton.label'),
      roomAddedLiveAnnounce: formatText('hotels.roomPicker.roomAdded.liveAnnounce', '{0}'),
      room: {
        __typename: 'RoomField',
        roomTitleAriaLabel: isSingleUnit ? '' : formatText('hotels.roomPicker.room.a11yLabelRoom', '{0}', '{1}'),
        roomTitle: isSingleUnit ? '' : formatText('hotels.roomPicker.room.title', '{0}'),
        adultsStepInput: {
          __typename: 'StepInput',
          title: formatText('hotels.roomPicker.adults.text'),
          increaseText: formatText('hotels.roomPicker.increaseAdults.text', '{0}'),
          decreaseText: formatText('hotels.roomPicker.decreaseAdults.text', '{0}'),
          caption: null,
        },
        childrenStepInput: {
          __typename: 'StepInput',
          title: formatText('hotels.roomPicker.children.text'),
          increaseText: formatText('hotels.roomPicker.increaseChildren.text', '{0}'),
          decreaseText: formatText('hotels.roomPicker.decreaseChildren.text', '{0}'),
          caption: formatText('hotels.roomPicker.children.caption'),
        },
        removeRoomButtonLabel: formatText('hotels.roomPicker.removeRoomButton.label'),
        removeRoomButtonAriaLabel: formatText('hotels.roomPicker.removeRoomButton.a11yLabel', '{0}', '{1}'),
        roomRemovedLiveAnnounce: formatText('hotels.roomPicker.roomRemoved.liveAnnounce', '{0}'),
        childrenAges: {
          __typename: 'ChildrenAges',
          selectChildAgeLabel: formatText('hotels.roomPicker.childAgeInput.label', '{0}'),
          youngestChildAgeText: formatText('hotels.roomPicker.youngestChildAge.text'),
          childAgeUpdatedLiveAnnounce: formatText('hotels.roomPicker.childUpdated.liveAnnounce', '{0}', '{1}'),
        },
      },
      petsRadioButtonGroup:
        isSingleUnit &&
        currentHotel?.showPetsOnSingleOffer &&
        ({
          __typename: 'PetsRadioButtonGroup',
          title: formatText('hotels.roomPicker.pets.title'),
          label: formatText('hotels.roomPicker.pets.label'),
          enableText: formatText('hotels.roomPicker.pets.include'),
          disableText: formatText('hotels.roomPicker.pets.exclude'),
          enabled: isPetsAmenityFilterPresent(context, currentHotel),
        } as any),
    },
  };

  return <TravelersField fieldName="travelers" data={travelersData} closeOnSubmit={closeOnSubmit} trigger={trigger} />;
});

export const SharedUiSubmitButtonWrapper: React.FC<{ text: string }> = ({ text }) => {
  const submitButtonData: SubmitButtonData = {
    __typename: 'LodgingFormSubmitButton',
    label: text,
  };

  return <SubmitButton fieldName="submit" data={submitButtonData} />;
};

export const SharedUiTravelersLinkWrapper: React.FC<{ currentHotel?: CurrentHotelStore; handleChange: (a: any) => void }> = inject(
  'currentHotel'
)(({ currentHotel, handleChange }) => {
  const { formatText } = useLocalization();
  const hotelId = currentHotel?.hotelId;
  const searchCriteria = currentHotel?.searchCriteria;
  const { context } = useBexApi();

  const inputs: FormInputs = React.useMemo(
    () => ({
      propertyId: String(hotelId),
      dateRange: searchCriteria ? getDateRangeInput(searchCriteria) : null,
      roomInput: searchCriteria ? getRooms(searchCriteria) : [],
    }),
    [hotelId, searchCriteria?.startDate, searchCriteria?.endDate, searchCriteria?.rooms]
  );

  const variable: FormQueryVariables = {
    ...(inputs || {}),
    context,
  };

  const doneLabel = formatText('hotels.infosite.searchForm.availabilityButton.label');

  return (
    <ActionProviderDatedMobile onChange={handleChange}>
      <FormInputsProvider value={variable}>
        <Experiment name="Vrbex_traveler_selector">
          <ExperimentControl>
            <SharedUiTravelersWrapper doneLabel={doneLabel} trigger={TravelersFieldTriggerType.LINK} closeOnSubmit={false} />
          </ExperimentControl>
          <ExperimentVariant bucket={1}>
            <SharedUiTravelersSelectorWrapper />
          </ExperimentVariant>
        </Experiment>
      </FormInputsProvider>
    </ActionProviderDatedMobile>
  );
});

SharedUiTravelersLinkWrapper.displayName = 'SharedUiTravelersLinkWrapper';
